/**
 * RecipeCard — A5-ish printable recipe card, formatted for wall-hang
 * or laminate use on a prep station. Rendered inside the global
 * PrintOverlay portal so `window.print()` on the current page prints
 * ONLY the card — no popup, no `window.open`.
 *
 * Ported from the previous `browserRecipePrint.js` popup HTML so the
 * paper output stays visually identical.
 */
import { allergenLabel } from "@/lib/allergens";

// Newlines OR comma-space (but not decimal). Keeps bullets neat when
// staff paste comma-separated ingredients on one line.
function splitLines(s) {
  if (!s) return [];
  return String(s)
    .split(/\r?\n|(?<=[^0-9])[,;] +/)
    .map((x) => x.trim())
    .filter(Boolean);
}

export default function RecipeCard({ recipe, siteName }) {
  const ingredients = splitLines(recipe.ingredients);
  const method = splitLines(recipe.method);
  const contains = (recipe.allergens || []).map((a) => allergenLabel(a));
  const mayContain = (recipe.may_contain || []).map((a) => allergenLabel(a));
  const cost = Number(recipe.cost_per_portion) || 0;
  const sell = Number(recipe.sell_price) || 0;
  const hasCost = cost > 0 && sell > 0;
  const foodPct = hasCost ? Math.round((100 * cost) / sell) : null;
  const margin = hasCost ? (sell - cost).toFixed(2) : "";
  const costBadgeColor =
    foodPct === null ? "#666"
      : foodPct <= 30 ? "#3A5A40"
        : foodPct <= 40 ? "#B45309"
          : "#BE4B2E";

  return (
    // `.recipe-card` styles live in index.css so they apply in print
    // media without needing an inline <style> block. `.print-area`
    // (added by PrintOverlay wrapper) is what the visibility trick
    // reveals when the LabelPrinter path is active, but for the
    // portal-based print (html.is-printing) we just need the card
    // itself since the app root is entirely hidden.
    <div className="recipe-card">
      <div className="recipe-card-header">
        {siteName ? <div className="rc-site">{siteName}</div> : null}
        <div className="rc-row">
          <div>
            <div className="rc-eyebrow">{recipe.category || "Recipe"}</div>
            <h1 className="rc-name">{recipe.name || "Untitled recipe"}</h1>
          </div>
          <div className="rc-shelf">
            <div className="rc-shelf-label">Shelf life</div>
            <div className="rc-shelf-value">
              {recipe.shelf_life_hours ?? "—"}h
            </div>
          </div>
        </div>
      </div>

      {(contains.length || mayContain.length) ? (
        <div className="rc-allergens">
          {contains.length ? (
            <div className="rc-al-row">
              <span className="rc-al-label">Contains</span>
              {contains.map((a) => (
                <span key={a} className="rc-chip rc-chip-solid">{a}</span>
              ))}
            </div>
          ) : null}
          {mayContain.length ? (
            <div className="rc-al-row">
              <span className="rc-al-label">May contain</span>
              {mayContain.map((a) => (
                <span key={a} className="rc-chip rc-chip-outline">{a}</span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {hasCost ? (
        <div className="rc-cost-strip">
          <div>
            <span className="rc-cost-label">Sell</span>
            <span className="rc-cost-value">${sell.toFixed(2)}</span>
          </div>
          <div>
            <span className="rc-cost-label">Cost</span>
            <span className="rc-cost-value">${cost.toFixed(2)}</span>
          </div>
          <div>
            <span className="rc-cost-label">Margin</span>
            <span className="rc-cost-value">${margin}</span>
          </div>
          <div
            className="rc-fc-pill"
            style={{ background: costBadgeColor, color: "#fff" }}
          >
            FC {foodPct}%
          </div>
          {recipe.portion_size ? (
            <div className="rc-portion-size">{recipe.portion_size}</div>
          ) : null}
        </div>
      ) : null}

      <div className="rc-cols">
        <section>
          <h2>Ingredients</h2>
          {ingredients.length ? (
            <ol>{ingredients.map((x, i) => <li key={i}>{x}</li>)}</ol>
          ) : (
            <p className="rc-muted">Add ingredients in Recipes → edit.</p>
          )}
        </section>
        <section>
          <h2>Method</h2>
          {method.length ? (
            <ol>{method.map((x, i) => <li key={i}>{x}</li>)}</ol>
          ) : (
            <p className="rc-muted">No method recorded.</p>
          )}
        </section>
      </div>

      <div className="rc-footer">
        <span>Printed via HACCP AU</span>
        <span>{new Date().toLocaleDateString("en-AU")}</span>
      </div>
    </div>
  );
}
